<template>
  <div id="home" :style="backgroundImage">
    <v-container fluid>
      <v-row justify="center">
        <span :class="bannerClass">
          <template v-if="!isGuest">
            {{ bannerText }}
          </template>
          <template v-else-if="isGuest">
            Welcome, <span class="font-weight-medium">{{ profile.nickname }}</span>!
          </template>
        </span>
      </v-row>

      <template v-if="authenticated && isGuest">
        <v-container>
          <v-row>
            <div class="mb-2">Don't have <b><i>Ambient Connect</i> ?</b></div>
          </v-row>
          <v-row>
            <a href="https://play.google.com/store/apps/details?id=aat.genie.remote" class="mb-2">
              <v-img :src="imageUri_DownloadGoogle" width="160" />
            </a>
          </v-row>
          <v-row>
            <a href="https://apps.apple.com/us/app/ambient-connect/id1527138807">
              <v-img :src="imageUri_DownloadApple" width="160" />
            </a>
          </v-row>
        </v-container>
      </template>

      <!-- Loading -->
      <loading
        :active.sync="isLoading"
        :can-cancel="false"
        loader="dots"
        :color="appColor_Progress"
      />

      <!-- Else: Authenticated -->
      <template v-if="!isLoading">

        <!-- Admin -->
        <template v-if="isAdmin">
          <v-toolbar dense flat dark :color="appColor_Medium">
            <v-toolbar-title class="font-weight-regular">
              <v-icon large class="mr-2">settings_applications</v-icon>Administrator Home
            </v-toolbar-title>

            <v-spacer />

            <v-toolbar-items>
              <v-btn @click="gotoRoute('AdminDashboard')" :color="appColor_Dark" :disabled="isLoadingKioskDashboard" small class="elevation-0">
                <v-icon class="mr-2">dashboard</v-icon>Dashboard
              </v-btn>
            </v-toolbar-items>
          </v-toolbar>
            
          <v-list class="pa-0">
            <v-list-item>
              <v-list-item-content>
                
                <v-list-item-title class="font-weight-light mb-0">
                  <span :class="isLoadingKioskOptions ? 'grey--text' : ''">Kiosk Dashboard Access</span>
                  <v-progress-circular v-if="isLoadingKioskOptions" width="2" size="15" class="ml-3" indeterminate color="grey" />
                </v-list-item-title>

                <v-list-item-subtitle>
                  <v-row no-gutters>
                    <template>
                      <!-- ABBY Dashboard Kiosks -->
                      <v-col cols="12" sm="6">
                        <kiosk-combobox
                          v-model="abbyKioskId"
                          :options="abbyKioskOptions"
                          :placeholder="'ABBY search...'"
                          :disabled="disableAbbyKioskCombo"
                          :dense="isSmallDevice"
                          :showButton="true"
                          :buttonColor="appColor_Abby"
                          :buttonIcon="'dashboard'"
                          :buttonLoading="isLoadingAbbyKioskDashboard"
                          @click="kioskComboClick(kioskTypeId_Abby)"
                          :class="isSmallDevice ? 'mb-2' : ''"
                          >
                        </kiosk-combobox>
                      </v-col>
      
                      <!-- GENIE Dashboard Kiosks -->
                      <v-col cols="12" sm="6">
                        <kiosk-combobox
                          v-model="genieKioskId"
                          :options="genieKioskOptions"
                          :placeholder="'GENIE search...'"
                          :disabled="disableGenieKioskCombo"
                          :dense="isSmallDevice"
                          :showButton="true"
                          :buttonColor="appColor_Genie"
                          :buttonIcon="'dashboard'"
                          :buttonLoading="isLoadingGenieKioskDashboard"
                          @click="kioskComboClick(kioskTypeId_Genie)"
                          :class="!isSmallDevice ? 'ml-4' : ''"
                        />
                      </v-col>
                    </template>
                  </v-row>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-divider />
          </v-list>

          <v-list class="pa-0">
            <v-list-item-group>
              <v-divider />
              <v-list-item @click="gotoRoute('FacilityManager')" :disabled="isLoadingKioskDashboard">
                <v-list-item-icon>
                  <v-icon large>location_city</v-icon>
                </v-list-item-icon>
                <v-list-item-title>
                  <span :class="adminMenuTextClass">Facilities</span>
                </v-list-item-title>
              </v-list-item>
              <v-divider />
              <v-list-item @click="gotoRoute('KioskManager')" :disabled="isLoadingKioskDashboard">
                <v-list-item-icon>
                  <v-icon large>computer</v-icon>
                </v-list-item-icon>
                <v-list-item-title>
                  <span :class="adminMenuTextClass">Kiosks</span>
                </v-list-item-title>
              </v-list-item>
              <v-divider />
              <v-list-item @click="gotoRoute('ResidentManager')" :disabled="isLoadingKioskDashboard">
                <v-list-item-icon>
                  <v-icon large>perm_identity</v-icon>
                </v-list-item-icon>
                <v-list-item-title>
                  <span :class="adminMenuTextClass">Residents</span>
                </v-list-item-title>
              </v-list-item>
              <v-divider />
              <v-list-item @click="gotoRoute('AdminUserManager')" :disabled="isLoadingKioskDashboard">
                <v-list-item-icon>
                  <v-icon large>people</v-icon>
                </v-list-item-icon>
                <v-list-item-title>
                  <span :class="adminMenuTextClass">Users</span>
                </v-list-item-title>
              </v-list-item>
              <v-divider />
              <v-list-item @click="gotoRoute('PublicMediaManager')" :disabled="isLoadingKioskDashboard">
                <v-list-item-icon>
                  <v-icon large :color="appColor_Genie">folder_shared</v-icon>
                </v-list-item-icon>
                <v-list-item-title :class="adminMenuTextClass">
                  <span :class="adminMenuTextClass">Genie Public Media</span>
                </v-list-item-title>
              </v-list-item>
              <v-divider />
              <v-list-item @click="gotoRoute('AbbySharedLibraryManager')" :disabled="isLoadingKioskDashboard">
                <v-list-item-icon>
                  <v-icon large :color="appColor_Abby">folder_shared</v-icon>
                </v-list-item-icon>
                <v-list-item-title>
                  <span :class="adminMenuTextClass">Abby Shared Library</span>
                </v-list-item-title>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </template>

        <!-- Resident Manager -->
        <template v-else-if="isResidentManager">
          <template v-if="hasMultipleResidents">
            <v-list class="pt-0 ma-4">
              
              <!-- List of Residents -->
              <v-toolbar flat color="grey lighten-4">
                <v-toolbar-title>
                  <span :class="toolbarTitleClass" v-html="getExpression('Heading', 'SelectManagedResident').replace('{0}', residents.length)"></span>
                </v-toolbar-title>
              </v-toolbar>

              <!--<span class="grey--text text--lighten-4">Welcome, <span class="font-italic font-weight-bold">{{ profile.nickname }}</span></span>-->

              <v-list-item-group
                v-for="(r, i) in residents"
                :key="r.id"
                >
                <v-list-item @click="gotoResidentDashboard(r)">
         
                  <!-- Avatar -->
                  <v-list-item-action>
                    <v-img width="100"
                      :src="r.pictureBase64 !== null
                          ? r.pictureBase64
                          : imageUri_NoAvatar"
                      >
                    </v-img>
                  </v-list-item-action>

                  <!-- Tombstone -->
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ r.name }}
                    </v-list-item-title>
                    <!--<v-list-item-icon v-if="r.isDefault && hasMultipleResidents && r.kiosk.typeId === kioskTypeId_Genie">
                      <v-icon color="amber" x-large>star</v-icon>
                    </v-list-item-icon>-->
                  </v-list-item-content>

                  <!-- Kiosk Type -->
                  <v-list-item-icon>
                    <v-img width="90" :src="getResidentKioskPicture(r)" class="mr-2" />
                  </v-list-item-icon>
                </v-list-item>
                <v-divider v-if="i < residents.length - 1" />
              </v-list-item-group>
            </v-list>
          </template>
        </template>

        <!-- Kiosk Manager -->
        <template v-else-if="isKioskManager">
          <template v-if="hasMultipleKiosks">
            <v-list class="pa-0 ma-4 elevation-2">
              
              <!-- List of Kiosks -->
              <v-toolbar flat color="grey lighten-4">
                <v-toolbar-title>
                  <span :class="toolbarTitleClass" v-html="getExpression('Heading', 'SelectManagedKiosk').replace('{0}', kiosks.length)"></span>
                </v-toolbar-title>

                <!-- <template v-if="isHSPPStatsUsers">
                  
                  <v-spacer />

                  <v-toolbar-items>
                    <v-btn
                      :color="appColor_Resident"
                      dark
                      class="elevation-0"
                      @click="gotoRoute('CiraStats')"
                      >
                        Facility Statistics
                    </v-btn>
                  </v-toolbar-items>
              
                </template> -->
              </v-toolbar>

              <v-list-item-group
                v-for="(k, i) in kiosks"
                :key="k.id"
                >
                <v-list-item two-line @click="gotoKioskDashboard(k)">

                  <!-- Avatar -->
                  <v-list-item-action>
                    <v-img :src="getKioskPicture(k)"></v-img>
                  </v-list-item-action>

                  <!-- Tombstone -->
                  <v-list-item-content>
                    <v-list-item-title>{{ k.serialNumber }}</v-list-item-title>
                    <v-list-item-subtitle>{{ k.name }}</v-list-item-subtitle>
                    <v-list-item-subtitle>{{ k.facility.name }}</v-list-item-subtitle>
                    <v-list-item-subtitle :class="subtitleClass">{{ k.facility.geographicalDescription }}</v-list-item-subtitle>
  
                    <template v-if="k.typeId === kioskTypeId_Abby">
                      <v-list-item-subtitle>
                        <v-chip outlined small :color="getSubscriptionColor(k.subscriptionLevel)" class="mr-3 mt-1">
                          {{ getSubscriptionDescription(k.subscriptionLevel) }}
                          <v-avatar v-if="k.subscriptionLevel >= subscriptionLevel_Basic">
                            <v-icon small right>mdi-checkbox-marked-circle</v-icon>
                          </v-avatar>
                          <v-avatar v-if="k.subscriptionLevel >= subscriptionLevel_Advanced" class="pl-0">
                            <v-icon small right>mdi-checkbox-marked-circle</v-icon>
                          </v-avatar>
                          <v-avatar v-if="k.subscriptionLevel >= subscriptionLevel_Unlimited" class="pl-0">
                            <v-icon small right>mdi-checkbox-marked-circle</v-icon>
                          </v-avatar>
                        </v-chip>
                      </v-list-item-subtitle>
                    </template>
                  </v-list-item-content>

                </v-list-item>
                <v-divider v-if="i < kiosks.length - 1" />
              </v-list-item-group>
            </v-list>
          </template>
        </template>
      </template>
    </v-container>
  </div>
</template>

<script>
import appService from '@/app.service.js'
import { mapGetters, mapActions } from 'vuex'
import { AppColor, AppStyle, FunctionalUserRole, KioskTypeId, ImageUri, SubscriptionLevel, UserIds } from '@/config.js'
import KioskCombobox from '@/components/KioskCombobox'

export default {
  name: this,
  components: {
    KioskCombobox
  },
  data() {
    return {
      // colors
      appColor_Light: AppColor.Light,
      appColor_Medium: AppColor.Medium,
      appColor_Dark: AppColor.Dark,
      appColor_Progress: AppColor.Progress,
      appColor_Genie: AppColor.KioskTypeGenie,
      appColor_Abby: AppColor.KioskTypeAbby,
      appColor_Resident: AppColor.ResidentLight,

      appColor_SubscriptionBasic: AppColor.SubscriptionBasic,
      appColor_SubscriptionAdvanced: AppColor.SubscriptionAdvanced,
      appColor_SubscriptionUnlimited: AppColor.SubscriptionUnlimited,

      // functional roles
      functionalRole_Admin: FunctionalUserRole.Admin,
      functionalRole_DomainManager: FunctionalUserRole.DomainManager,
      functionalRole_KioskManager: FunctionalUserRole.KioskManager,
      functionalRole_ResidentManager: FunctionalUserRole.ResidentManager,
      functionalRole_Guest: FunctionalUserRole.Guest,

      // kiosk types
      kioskTypeId_Abby: KioskTypeId.Abby,
      kioskTypeId_Genie: KioskTypeId.Genie,

      // image uri
      imageUri_Background: ImageUri.Background,
      imageUri_BackgroundFaint: ImageUri.BackgroundFaint,
      imageUri_NoAvatar: ImageUri.NoAvatar,
      imageUri_GenieKiosk: ImageUri.GenieKiosk,
      imageUri_AbbyKiosk: ImageUri.AbbyKiosk,
      imageUri_DownloadGoogle: ImageUri.DownloadGoogle,
      imageUri_DownloadApple: ImageUri.DownloadApple,
      
      // subscription levels
      subscriptionLevel_Basic: SubscriptionLevel.Basic,
      subscriptionLevel_Advanced: SubscriptionLevel.Advanced,
      subscriptionLevel_Unlimited: SubscriptionLevel.Unlimited,

      // users
      user_HSPPStats: UserIds.HSPPStatsUsers,

      // styles
      appStyle_HyperLink: AppStyle.HyperLink,

      // kiosk dashboard access 
      kioskOptions: [],
      abbyKioskId: null,
      genieKioskId: null,
      isLoadingKioskOptions: true,
      isLoadingAbbyKioskDashboard: false,
      isLoadingGenieKioskDashboard: false,

      adminMenuIconColor: 'amber darken-1',
      adminMenuTextClass: 'text-h5 font-weight-light',

      isAuthenticated: this.authenticated,
      isLoading: false,

      // error handling
      errorDialog: false,
      errorTitle: 'Internal Error',
      errorText: null
    }
  },
  computed: {
    ...mapGetters ([
      'authenticated', 
      'profile',
      'residents',
      'kiosks'
    ]),
    backgroundImage () {
      const url = !this.authenticated || this.isGuest
        ? this.imageUri_Background
        : this.imageUri_BackgroundFaint

      return `background-image: url(${url});`
    },
    bannerText () {
      return this.getExpression('Label', 'AmbientAccess')
    },
    isAdmin () {
      if (this.profile.functionalRole === undefined) return false
      return this.profile.functionalRole === this.functionalRole_Admin
    },
    isGuest () {
      if (this.profile.functionalRole === undefined) return false
      return this.profile.functionalRole === this.functionalRole_Guest
    },
    isResidentManager () {
      if (this.profile.functionalRole === undefined) return false
      return this.profile.functionalRole === this.functionalRole_ResidentManager
    },
    isKioskManager () {
      if (this.profile.functionalRole === undefined) return false
      return (
        this.profile.functionalRole === this.functionalRole_KioskManager ||
        this.profile.functionalRole === this.functionalRole_DomainManager
      )
    },
    isDomainManager () {
      if (this.profile.functionalRole === undefined) return false
      return (
        this.profile.functionalRole === this.functionalRole_DomainManager
      )
    },
    hasMultipleResidents () {
      if (this.residents === null) return false
      return this.isResidentManager && this.residents.length > 1
    },
    hasMultipleKiosks () {
      if (this.kiosks === null) return false
      return this.isKioskManager && this.kiosks.length > 1
    },
    genieKioskOptions () {
      const self = this
      return self.kioskOptions
        .filter(k => k.typeId === self.kioskTypeId_Genie)
    },
    abbyKioskOptions () {
      const self = this
      return self.kioskOptions
        .filter(k => k.typeId === self.kioskTypeId_Abby)
    },
    disableAbbyKioskCombo () {
      return this.abbyKioskOptions.length <= 0 || this.isLoadingKioskDashboard
    },
    disableGenieKioskCombo () {
      return this.genieKioskOptions.length <= 0 || this.isLoadingKioskDashboard
    },
    isLoadingKioskDashboard () {
      return this.isLoadingAbbyKioskDashboard || this.isLoadingGenieKioskDashboard
    },
    disableAbbyDashboardButton () {
      return this.abbyKioskId <= 0 || this.abbyKioskId === null
    },
    disableGenieDashboardButton () {
      return this.genieKioskId <= 0 || this.genieKioskId === null
    },
    bannerClass () {
      const marginLeft = 'ml-4'
      const fontSmall = 'headline'
      const fontLarge = 'display-2'
      const common = 'text-xs-center font-weight-black blue-grey--text text--lighten-5'
      return this.isSmallDevice
        ? `${marginLeft} ${fontSmall} ${common} mb-0`
        : `${marginLeft} ${fontLarge} ${common} mb-10`
    },
    titleClass () {
      return 'body-1 font-weight-regular'
    },
    subtitleClass () {
      return 'grey--text lighten-2 font-weight-light'
    },
    toolbarTitleClass () {
      return this.isSmallDevice
        ? 'font-weight-light body-1 black--text'
        : 'font-weight-light black--text'
    },
    auth0Client () {
      return this.$auth.auth0Client
    },
    // temporary stats for HSPP Stats Users
    isHSPPStatsUsers () {
      if (this.isNullOrUndefined(this.profile)) return false
      return this.user_HSPPStats.includes(this.profile.id) 
    }
  },
  methods: {
    ...mapActions(['login', 'logout']),

    loadData () {
      if (!this.isValidSession) return

      const self = this
      self.initStore()
  
      if (self.isKioskManager || self.isDomainManager) {
        if (self.kiosks === null) {
          self.isLoading = true

          // retrieve & store managed kiosks
          appService.getKiosksForLogin(self.profile).then(data => {
            if (data.errorSeverity > 0) {
              self.handleError(data)
            } else {
              self.$store.dispatch('storeLoginData', { loginData: {
                residents: null,
                kiosks: data.kiosks,
                isAbby: data.isAbby,
                isGenie: data.isGenie,
                languages: data.languages,
                isAdmin: false,
                isDomainManager: self.isDomainManager,
                isKioskManager: self.isKioskManager,
                isResidentManager: false
              }})
              self.handleKioskManager()

              // user doesn't exist warning
              if (data.errorSeverity === 0) {
                self.$error.show({
                  title: 'Warning',
                  text: data.errorMessage,
                  severity: data.errorSeverity
                })
              }
            }
            self.isLoading = false
          })
        } else {
          self.handleKioskManager()
        }

      //-- resident manager
      } else if (self.isResidentManager) {
        if (self.residents === null) {
          self.isLoading = true

          // store managed residents
          appService.getResidentsForLogin(self.profile).then(data => {
            if (data.errorSeverity > 0) {
              self.handleError(data)
            } else {
              self.$store.dispatch('storeLoginData', { loginData: {
                residents: data.residents,
                kiosks: null,
                isAbby: data.isAbby,
                isGenie: data.isGenie,
                languages: data.languages,
                isAdmin: false,
                isDomainManager: false,
                isKioskManager: false,
                isResidentManager: true
              }})
              self.handleResidentManager()
            }

            // user doesn't exist warning
            if (data.errorSeverity === 0) {
              self.$error.show({
                title: 'Warning',
                text: data.errorMessage,
                severity: data.errorSeverity
              })
            }

            self.isLoading = false
          }).catch(e => { self.handleCriticalError(e) })
        } else {
          self.handleResidentManager()
        }

      // admin
      } else {
        self.isLoadingKioskOptions = true
        appService.getForAdminLogin(self.profile).then(data => {
          if (data.errorSeverity > 0) {
            self.isLoadingKioskOptions = false
            self.handleError(data)
          } else {
            self.$store.dispatch('storeLoginData', { loginData: {
              languages: data.languages,
              residents: null,
              kiosks: null,
              facilities: null,
              isAbby: true,
              isGenie: true,
              isAdmin: true,
              isKioskManager: false,
              isResidentManager: false,
              isDomainManager: false
            }})

            // user doesn't exist warning
            if (data.errorSeverity === 0) {
              self.$error.show({
                title: 'Warning',
                text: data.errorMessage,
                severity: data.errorSeverity
              })
            }

            // get kiosk combobox options
            appService.getKioskComboboxOptions().then(data => {
              self.isLoadingKioskOptions = false
              if (data.errorSeverity > 0) {
                self.handleError(data)
              } else {
                self.kioskOptions = data.options

                // give the combobox time to render before initializing
                setTimeout(() => {
                  self.abbyKioskId = 0
                  self.genieKioskId = 0
                }, 300)
              }
            })
          }
        })
      }
    },

    initStore () {
      this.$store.dispatch('initRouteStack')
      if (this.isAdmin || this.hasMultipleKiosks) {
        this.$store.dispatch('clearActiveKiosk')
        this.$store.dispatch('clearKioskResidents')
        this.$store.dispatch('clearKioskContacts')
        this.$store.dispatch('clearKioskKeyNums')
        this.$store.dispatch('clearActiveResident')
      }
      if  (this.isAdmin || this.hasMultipleResidents) {
        this.$store.dispatch('clearActiveResident')
      }
    },

    handleResidentManager () {
      if (this.residents.length === 1) {

        // pin them to the dashboard with a single kiosk
        this.gotoResidentDashboard(this.residents[0])
      }
    },

    handleKioskManager () {
      if (this.kiosks.length === 1) {

        // pin them to the dashboard with a single kiosk
        this.gotoKioskDashboard(this.kiosks[0])
      }
    },

    gotoRoute (name) {
      this.$store.dispatch('initRouteStack')
      this.$store.dispatch('pushRoute', { name: name })
      this.$router.push({ name: name })
    },

    gotoResidentDashboard (resident) {
      this.$store.dispatch('setActiveResident', { activeResident: resident })
      if (this.hasMultipleResidents) {
        this.$store.dispatch('pushRoute', { name: 'ResidentDashboard' })
      }
      this.$router.push({ name: 'ResidentDashboard' })
    },

    gotoKioskDashboard (kiosk) {
      if (kiosk.subscriptionLevel === this.subscriptionLevel_Basic) {
        this.$alert.show({
          title: this.getExpression('FormTitle', 'AccessDenied'),
          text: this.getExpression('Alert', 'AbbySubscriptionAccessDenied'),
          formColor: 'yellow darken-3',
          icon: 'warning'
        })
      } else {
        this.$store.dispatch('setActiveKiosk', { activeKiosk: kiosk })
        if (this.hasMultipleKiosks) {
          this.$store.dispatch('pushRoute', { name: 'KioskDashboard' })
        }
        this.$router.push({ name: 'KioskDashboard' })
      }
    },

    kioskComboClick (kioskTypeId) {
      const self = this
      const isAbby = kioskTypeId === self.kioskTypeId_Abby
      const isGenie = kioskTypeId === self.kioskTypeId_Genie

      // kioskId
      var kioskId = null
      if (isAbby) kioskId = self.abbyKioskId
      else if (isGenie) kioskId = self.genieKioskId

      if (kioskId > 0) {     
        if (isAbby) self.isLoadingAbbyKioskDashboard = true
        else if (isGenie) self.isLoadingGenieKioskDashboard = true
        
        // get extended kiosk data for the dashboard
        appService.getKioskExtended(kioskId).then(data => {
          if (data.errorSeverity > 0) {
            self.handleError(data)
          } else {
            const kiosk = data.kiosk

            // navigate to its dashboard
            self.$store.dispatch('clearActiveKiosk')
            self.$store.dispatch('clearKioskResidents')
            self.$store.dispatch('clearKioskContacts')
            self.$store.dispatch('clearKioskKeyNums')
            self.$store.dispatch('setActiveKiosk', { activeKiosk: kiosk })
            self.$store.dispatch('pushRoute', { name: 'KioskDashboard' })
            self.$router.push({ name: 'KioskDashboard' })
          }
          self.isLoadingAbbyKioskDashboard = false
          self.isLoadingGenieKioskDashboard = false
        })
      }
    },

    getKioskPicture (kiosk) {
      // !this.isNullOrUndefined(kiosk.pictureUri)
      //   ? kiosk.pictureUri
      return kiosk.typeId == this.kioskTypeId_Genie
          ? this.imageUri_GenieKiosk
          : this.imageUri_AbbyKiosk
    },

    getResidentKioskPicture (resident) {
      return resident.kiosk.typeId == this.kioskTypeId_Genie
          ? this.imageUri_GenieKiosk
          : this.imageUri_AbbyKiosk
    },

    getKioskFormalName (kiosk) {
      return kiosk.facility === null
        ? kiosk.name
        : `${kiosk.name} (${kiosk.facility.code})`
    },

    getKioskTypeColor (typeId) {
      if (typeId === this.kioskTypeId_Abby) {
        return this.appColor_Abby
      } else if (typeId === this.kioskTypeId_Genie) {
        return this.appColor_Genie
      } else return 'grey'
    },

    getSubscriptionDescription (level) {
      var description = ''
      if (level === this.subscriptionLevel_Basic) {
        description = this.getExpression('Label', 'Basic') 
      } else if (level === this.subscriptionLevel_Advanced) {
        description = this.getExpression('Label', 'Advanced')
      } else if (level === this.subscriptionLevel_Unlimited) {
        description = this.getExpression('Label', 'Unlimited')
      }
      return description
    },

    getSubscriptionColor (level) {
      if (level === this.subscriptionLevel_Basic) return this.appColor_SubscriptionBasic
      if (level === this.subscriptionLevel_Advanced) return this.appColor_SubscriptionAdvanced
      if (level === this.subscriptionLevel_Unlimited) return this.appColor_SubscriptionUnlimited
      return 'grey'
    },

    handleError (result) {
      this.$error.show({
        title: result.errorSeverity === 0
          ? 'Warning'
          : 'Internal Error',
        text: result.errorMessage,
        severity: result.errorSeverity
      })
    }
  },
  created() {
    this.loadData()
  },
  watch: {
    isValidSession (val) {
      if (val === null) return
      if (val) {
        this.loadData()
      }
    }
  }
}
</script>

<style>
#home {
  background-size: cover;
  background-attachment: fixed;
  height: 90vh;
}
</style>
